import { useEffect } from 'react';
import {
  DatepickerSimple,
  Select,
  CURRENCY_LIST_OPTIONS,
  CA_CURRENCY_ONLY,
  US_CURRENCY_ONLY,
  UploadFile,
  Timestamp,
  BankFileConversionRail,
  BankFileConversionType,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { FieldData } from '@/components/Form/types';
import * as Yup from 'yup';
import { format } from 'date-fns';
import useFileConversion from '@/hooks/useApi/useFileConversion';

export type ConvertMyTemplateFormValues = {
  Service: 'debit' | 'credit';
  Product: BankFileConversionRail | '';
  Currency: string;
  FileBase64: string;
  FileName: string;
  Bank: BankFileConversionType | '';
  ValueDate: string;
  FallbackPaymentRail: BankFileConversionRail | '';
};

export const useConvertMyTemplateFormFields = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { getConfigurations, configurationsLoading, configurations } = useFileConversion();
  useEffect(() => {
    getConfigurations().catch((err) => console.error(err));
  }, []);
  const ServiceOptions = [
    { value: 'debit', label: tGlobal('ServiceOptions.debit') },
    { value: 'credit', label: tGlobal('ServiceOptions.credit') },
  ];

  const getBankOptions = () => {
    return configurations.map((config) => ({
      value: config.FileType,
      label: config.FinancialInstitution,
    }));
  };

  const getProductOptions = (bank: string, service: string) => {
    // Find the configuration for the selected bank
    const bankConfig = configurations.find((config) => config.FileType === bank);
    if (!bankConfig) return [];

    // Get payment rails for the selected service
    const serviceKey = service === 'credit' ? 'Credit' : service === 'debit' ? 'Debit' : null;
    if (!serviceKey) return [];

    const paymentRails = bankConfig.PaymentRails[serviceKey] || [];

    // Map payment rail codes to readable names
    const railNames: Record<string, string> = {
      ach: tGlobal('PaymentRail.ACH'),
      rtp: tGlobal('PaymentRail.RTP'),
      wire: tGlobal('PaymentRail.WIRE'),
      eft: tGlobal('PaymentRail.EFT'),
      vopayinstant: tGlobal('PaymentRail.VopayInstant'),
    };

    return paymentRails.map((rail) => ({
      value: rail,
      label: railNames[rail] || rail.toUpperCase(),
    }));
  };

  const getFallbackPaymentRailOptions = (bank: string, service: string, selectedProduct: string) => {
    const allOptions = getProductOptions(bank, service);
    // Return all options except the currently selected product
    return allOptions.filter((option) => option.value !== selectedProduct);
  };

  const details: FieldData<ConvertMyTemplateFormValues>[] = [
    {
      fieldName: 'FileBase64',
      label: () => t('fileLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['FileBase64']>({});
        return (
          <UploadFile
            id="FileBase64"
            {...data}
            variant="onboarding"
            onBlurFileName={(e: React.FocusEvent<HTMLInputElement>) => ctx.handleBlur(e)}
            onChangeFileName={(e: React.ChangeEvent<HTMLInputElement>) => ctx.setFieldValue('FileName', e.target.value)}
            idFileName="FileName"
            valueFileName={ctx.values.FileName}
            maxMBSize={20}
            validExtensions={['csv', 'txt', 'xlsx', 'xls', 'odf', 'tsv', 'xml', 'json']}
            accept=".csv,.txt,.xlsx,.xls,.odf,.tsv,.xml,.json"
            keepExtension={true}
          />
        );
      },
    },
  ];

  const convertTo: FieldData<ConvertMyTemplateFormValues>[] = [
    {
      fieldName: 'Bank',
      label: () => t('bankLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<ConvertMyTemplateFormValues['Bank']>({})}
          options={getBankOptions()}
          placeholder=""
          sort={false}
          loading={configurationsLoading}
        />
      ),
    },
    {
      fieldName: 'Service',
      label: () => t('serviceLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<ConvertMyTemplateFormValues['Service']>({})}
          options={ServiceOptions}
          placeholder=""
          sort={false}
          loading={configurationsLoading}
        />
      ),
    },
    {
      fieldName: 'Product',
      label: () => t('productLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        return (
          <Select
            version="2"
            {...getFormikData<ConvertMyTemplateFormValues['Product']>({})}
            options={getProductOptions(ctx.values.Bank, ctx.values.Service)}
            loading={configurationsLoading}
            placeholder=""
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'Currency',
      label: () => t('currencyLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        let currencyOptions = Object.entries(CURRENCY_LIST_OPTIONS).map(([value, label]) => ({
          value,
          label,
        }));
        const product = (ctx.values.Product || '').toLocaleLowerCase();
        if (product === 'ach' || product === 'rtp' || product === 'wire') {
          currencyOptions = Object.entries(US_CURRENCY_ONLY).map(([value, label]) => ({
            value,
            label,
          }));
        } else {
          const sourceOptions = product !== 'eft' ? CA_CURRENCY_ONLY : CURRENCY_LIST_OPTIONS;
          currencyOptions = Object.entries(sourceOptions).map(([value, label]) => ({
            value,
            label,
          }));
        }

        return (
          <Select
            version="2"
            {...getFormikData<ConvertMyTemplateFormValues['Currency']>({})}
            options={currencyOptions}
            placeholder=""
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'ValueDate',
      label: () => t('valueDateLabel'),
      getInputState: () => 'required',
      serializeField: (values) => {
        const endDate = new Date(Number(values.ValueDate));
        return format(endDate, 'yyyy-MM-dd');
      },
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['ValueDate']>({});
        return (
          <DatepickerSimple
            variant="outlineShadow"
            position="topLeft"
            {...data}
            min={new Date().getTime() as Timestamp}
            isDisabled={data.disabled}
          />
        );
      },
    },
    {
      fieldName: 'FallbackPaymentRail' as keyof ConvertMyTemplateFormValues,
      label: () => t('fallbackPaymentRailLabel'),
      getInputState: () => 'optional',
      showField: () => true,

      renderField: ({ getFormikData, ctx }) => (
        <Select
          version="2"
          {...getFormikData<ConvertMyTemplateFormValues['FallbackPaymentRail']>({})}
          options={getFallbackPaymentRailOptions(ctx.values.Bank, ctx.values.Service, ctx.values.Product)}
          placeholder=""
          sort={false}
          loading={configurationsLoading}
        />
      ),
    },
  ];

  return { details, convertTo, getProductOptions, getFallbackPaymentRailOptions, getBankOptions };
};
